import React from 'react';
import PropTypes from 'prop-types';
import { useParams } from 'react-router-dom';

import MenuItemDetailsModal from 'catering/components/Menu/MenuItemDetailsModal';
import MenuItemModalFooter from 'catering/components/Menu/MenuItemModalFooter';
import MenuItemDetailsForm from 'catering/components/Forms/MenuItemDetailsForm';
import PageLoadSpinner from 'components/PageLoadSpinner';

import { useGoToMenuScreen } from 'catering/hooks/navigation';
import {
  useCart,
  useCateringLocationBySlug,
  useMenuItemDetailsBySlug,
  useAddToCart,
  selectItemQuantity
} from 'catering/hooks/query';
import { useTrackEvent } from 'catering/hooks';
import { standaloneErrorMessages } from 'catering/constants/errors';
import {
  isCartCouldNotBeFoundError,
  isItemToAddSoldOut,
  isItemToAddWouldExceedInventory
} from 'catering/helpers';
import {
  useOpenLeadTimesChangedNotificationModal,
  useOpenMenuItemUnavailableNotificationModal,
  useOpenCartNoLongerExistsNotificationModal,
  useOpenMenuItemInventoryNotificationModal
} from 'catering/components/Modals/NotificationModals';
import { MENU_ITEM } from 'constants/menu/formTypes';
import { buildCateringFormModifications } from 'helpers/forms/menuItem';
import useGetModifierGroups from 'hooks/useGetModifierGroups';
import { PRODUCT_VARIANT_FIELD_NAME } from 'constants/menu/menuItemDetailForm';

const INITIAL_MENU_ITEM_FORM_VALUES = { quantity: 1 };

function MenuItemDetailsContainer({ setAlertContent, conversionMethod }) {
  const goToMenuScreen = useGoToMenuScreen();
  const { locationSlug, menuItemSlug } = useParams();

  const {
    data: menuItemDetails,
    isLoading: isLoadingMenuItemDetails
  } = useMenuItemDetailsBySlug(menuItemSlug);

  const { data: cart, isLoading: isCartLoading } = useCart({
    select: cart => ({
      ...cart,
      itemQuantity: selectItemQuantity(cart, menuItemSlug)
    })
  });

  const cateringLocationId = cart ? cart.catering_location : undefined;
  const trackEvent = useTrackEvent(cateringLocationId);

  const {
    data: cateringLocation,
    isLoading: isLoadingCateringLocation
  } = useCateringLocationBySlug(locationSlug);

  const addToCart = useAddToCart();

  const openLeadTimesChangedNotificationModal = useOpenLeadTimesChangedNotificationModal();
  const openMenuItemUnavailableNotificationModal = useOpenMenuItemUnavailableNotificationModal();
  const openCartNoLongerExistsNotificationModal = useOpenCartNoLongerExistsNotificationModal();
  const openMenuItemInventoryNotificationModal = useOpenMenuItemInventoryNotificationModal();

  const modifierGroups = useGetModifierGroups(menuItemDetails);

  const handleSubmit = formState => {
    const { id: productId } = menuItemDetails;

    const {
      quantity,
      'order-made-for': orderMadeFor,
      'special-instructions': specialInstructions,
      [PRODUCT_VARIANT_FIELD_NAME]: variant_id,
      ...modifications
    } = formState;

    const payload = {
      quantity,
      product_id: productId,
      order_made_for: orderMadeFor,
      special_instructions: specialInstructions,
      conversion_method: conversionMethod,
      required_fields: {
        ...buildCateringFormModifications(modifierGroups, modifications)
      }
    };

    if (variant_id) {
      payload.variant_id = variant_id;
    }

    addToCart.mutate(payload, {
      onSuccess: updatedCart => {
        trackEvent({
          category: 'Add to Cart',
          action: 'Click',
          label: 'eCom',
          value: cart.itemQuantity || 1
        });
        setAlertContent({
          isOpen: true,
          locationSlug: locationSlug,
          item: {
            name: menuItemDetails.name,
            image: menuItemDetails.images[0]
          },
          cart: {
            quantity: updatedCart.quantity,
            subtotal: updatedCart.subtotal,
            items: updatedCart.items
          }
        });
        goToMenuScreen();
      },
      onError: ({ message }) => {
        if (
          message === standaloneErrorMessages.ITEM_NOT_AVAILABLE_AT_THIS_TIME
        ) {
          goToMenuScreen();
          openLeadTimesChangedNotificationModal();
        } else if (isCartCouldNotBeFoundError(message)) {
          goToMenuScreen();
          openCartNoLongerExistsNotificationModal();
        } else if (
          isItemToAddSoldOut(message) ||
          isItemToAddWouldExceedInventory(message)
        ) {
          goToMenuScreen();
          openMenuItemInventoryNotificationModal({ message });
        }
      }
    });
  };

  if (isLoadingMenuItemDetails || isCartLoading || isLoadingCateringLocation) {
    return <PageLoadSpinner />;
  }

  if (menuItemDetails && !menuItemDetails.id) {
    goToMenuScreen();
    openMenuItemUnavailableNotificationModal();

    return <></>;
  }

  return (
    <MenuItemDetailsModal
      menuItemDetails={menuItemDetails}
      menuItemDetailsForm={
        <MenuItemDetailsForm
          cart={cart}
          cateringLocation={cateringLocation}
          formType={MENU_ITEM}
          FooterWrapperComponent={MenuItemModalFooter}
          menuItemDetails={menuItemDetails}
          onSubmit={handleSubmit}
          initialValues={INITIAL_MENU_ITEM_FORM_VALUES}
          isAddingToCart={addToCart.isLoading}
          modifierGroups={modifierGroups}
        />
      }
    />
  );
}

MenuItemDetailsContainer.propTypes = {
  setAlertContent: PropTypes.func.isRequired,
  conversionMethod: PropTypes.string
};

export default MenuItemDetailsContainer;
